import React, { useRef, useState, useCallback } from "react";
import { Button } from "./ui/button";
import type { AnalysisResult } from "../api/mockData";

interface UploadScreenProps {
  onPhotosSelected: (files: File[]) => void;
  history: AnalysisResult[];
  onOpenResult: (id: string) => void;
}

const MAX_PHOTOS = 10;

export function UploadScreen({ onPhotosSelected, history, onOpenResult }: UploadScreenProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const sliderRef = useRef<HTMLDivElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [previewUrls, setPreviewUrls] = useState<string[]>([]);

  const addFiles = useCallback((files: FileList | File[]) => {
    const newFiles = Array.from(files).filter((f) => f.type.startsWith("image/"));
    setSelectedFiles((prev) => {
      const combined = [...prev, ...newFiles].slice(0, MAX_PHOTOS);
      const urls = combined.map((f) => URL.createObjectURL(f));
      // Revoke old URLs
      previewUrls.forEach((u) => URL.revokeObjectURL(u));
      setPreviewUrls(urls);
      return combined;
    });
  }, [previewUrls]);

  const removePhoto = useCallback((index: number) => {
    setSelectedFiles((prev) => {
      const next = prev.filter((_, i) => i !== index);
      URL.revokeObjectURL(previewUrls[index]);
      setPreviewUrls((urls) => urls.filter((_, i) => i !== index));
      return next;
    });
  }, [previewUrls]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) addFiles(e.target.files);
    e.target.value = "";
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files) addFiles(e.dataTransfer.files);
  };

  const handleAnalyze = () => {
    if (selectedFiles.length > 0) onPhotosSelected(selectedFiles);
  };

  const formatDate = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleDateString("ru-RU", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
  };

  return (
    <div className="min-h-[100dvh] flex flex-col relative overflow-hidden">
      {/* Background orbs */}
      <div
        className="absolute top-[-60px] right-[-40px] w-[200px] h-[200px] rounded-full animate-float opacity-30"
        style={{ background: "radial-gradient(circle, var(--rose-light) 0%, transparent 70%)" }}
      />
      <div
        className="absolute bottom-[120px] left-[-60px] w-[180px] h-[180px] rounded-full animate-float opacity-20"
        style={{ background: "radial-gradient(circle, var(--gold-light) 0%, transparent 70%)", animationDelay: "3s" }}
      />
      <div
        className="absolute top-[40%] right-[-30px] w-[120px] h-[120px] rounded-full animate-float opacity-15"
        style={{ background: "radial-gradient(circle, var(--mauve-light) 0%, transparent 70%)", animationDelay: "5s" }}
      />

      {/* Content */}
      <div className="flex-1 flex flex-col items-center px-6 relative z-10 pt-14">
        {/* Logo mark */}
        <div className="animate-fade-up mb-6">
          <div
            className="w-20 h-20 rounded-[24px] flex items-center justify-center relative"
            style={{
              background: "linear-gradient(145deg, var(--rose) 0%, var(--mauve) 100%)",
              boxShadow: "0 12px 40px oklch(0.48 0.14 12 / 0.25)"
            }}
          >
            <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
              <path
                d="M20 4C20 4 8 14 8 22C8 28.627 13.373 34 20 34C26.627 34 32 28.627 32 22C32 14 20 4 20 4Z"
                fill="white"
                fillOpacity="0.9"
              />
              <path
                d="M20 10C20 10 14 17 14 22C14 25.314 16.686 28 20 28C23.314 28 26 25.314 26 22C26 17 20 10 20 10Z"
                fill="white"
                fillOpacity="0.4"
              />
            </svg>
          </div>
        </div>

        <h1
          className="font-editorial text-[44px] leading-[1.05] tracking-[-0.02em] text-center animate-fade-up delay-150"
          style={{ fontWeight: 300 }}
        >
          <span className="text-gradient-rose">Kibbe</span>
          <br />
          <span style={{ color: "var(--foreground)" }}>Анализ</span>
        </h1>

        <p
          className="font-body text-[15px] leading-[1.5] text-center mt-4 max-w-[300px] animate-fade-up delay-300"
          style={{ color: "var(--muted-foreground)", fontWeight: 400 }}
        >
          Загрузите фотографии для определения вашего типажа по системе Кибби
        </p>

        {/* Upload hint */}
        <div
          className="mt-5 w-full max-w-[320px] animate-fade-up delay-400 rounded-[14px] p-3.5 flex gap-3 items-start"
          style={{ background: "var(--gold-light)", border: "1px solid oklch(0.72 0.10 75 / 0.3)" }}
        >
          <div className="shrink-0 mt-0.5">
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
              <circle cx="9" cy="9" r="7.5" stroke="var(--gold)" strokeWidth="1.3" />
              <path d="M9 5.5V9.5M9 12V12.01" stroke="var(--gold)" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </div>
          <div>
            <div className="font-body text-[13px] font-semibold" style={{ color: "var(--foreground)" }}>
              Для точного анализа загрузите:
            </div>
            <ul className="font-body text-[12px] mt-1 space-y-0.5" style={{ color: "var(--secondary-foreground)" }}>
              <li>📸 Фото лица крупным планом (анфас)</li>
              <li>🧍 Фото в полный рост (спереди)</li>
              <li>Можно добавить до {MAX_PHOTOS} фото</li>
            </ul>
          </div>
        </div>

        {/* Photo slider (when photos are selected) */}
        {previewUrls.length > 0 && (
          <div className="mt-5 w-full max-w-[340px] animate-fade-in-scale">
            <div className="flex items-center justify-between mb-2 px-1">
              <span className="font-body text-[13px] font-medium" style={{ color: "var(--foreground)" }}>
                Выбрано фото: {previewUrls.length}/{MAX_PHOTOS}
              </span>
              {selectedFiles.length < MAX_PHOTOS && (
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="font-body text-[13px] font-medium"
                  style={{ color: "var(--rose)" }}
                >
                  + Ещё
                </button>
              )}
            </div>
            <div
              ref={sliderRef}
              className="flex gap-3 overflow-x-auto pb-3 snap-x snap-mandatory"
              style={{ scrollbarWidth: "none", WebkitOverflowScrolling: "touch" }}
            >
              {previewUrls.map((url, i) => (
                <div
                  key={i}
                  className="relative shrink-0 snap-start rounded-[16px] overflow-hidden group"
                  style={{ width: 110, height: 146 }}
                >
                  <img
                    src={url}
                    alt={`Фото ${i + 1}`}
                    className="w-full h-full object-cover"
                  />
                  <button
                    onClick={() => removePhoto(i)}
                    className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                    style={{ background: "oklch(0 0 0 / 0.5)", backdropFilter: "blur(4px)" }}
                  >
                    <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                      <path d="M2 2L8 8M8 2L2 8" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
                    </svg>
                  </button>
                  {/* Always-visible delete on mobile (no hover) */}
                  <div
                    className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full flex items-center justify-center md:hidden"
                    style={{ background: "oklch(0 0 0 / 0.45)" }}
                    onClick={() => removePhoto(i)}
                  >
                    <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                      <path d="M2 2L8 8M8 2L2 8" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
                    </svg>
                  </div>
                  <div
                    className="absolute bottom-0 left-0 right-0 py-1 text-center font-body text-[10px] font-medium text-white"
                    style={{ background: "oklch(0 0 0 / 0.4)" }}
                  >
                    {i + 1}
                  </div>
                </div>
              ))}

              {/* Add more placeholder */}
              {selectedFiles.length < MAX_PHOTOS && (
                <div
                  className="shrink-0 snap-start rounded-[16px] flex flex-col items-center justify-center cursor-pointer transition-all duration-200 hover:scale-[1.03] active:scale-[0.97]"
                  style={{
                    width: 110,
                    height: 146,
                    border: "2px dashed var(--border)",
                    background: "var(--warm-white)",
                  }}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    <path d="M12 5V19M5 12H19" stroke="var(--muted-foreground)" strokeWidth="1.5" strokeLinecap="round" />
                  </svg>
                  <span className="font-body text-[11px] mt-1" style={{ color: "var(--muted-foreground)" }}>
                    Добавить
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Upload area (when no photos yet) */}
        {previewUrls.length === 0 && (
          <div
            className={`mt-6 w-full max-w-[320px] animate-fade-up delay-500 transition-all duration-300 ${
              dragOver ? "scale-[1.02]" : ""
            }`}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
          >
            <div
              className="rounded-[20px] p-8 flex flex-col items-center cursor-pointer group transition-all duration-300 hover:scale-[1.01]"
              style={{
                border: dragOver ? "2px solid var(--rose)" : "2px dashed var(--border)",
                background: dragOver ? "var(--rose-mist)" : "var(--warm-white)",
              }}
              onClick={() => fileInputRef.current?.click()}
            >
              {/* Upload icon */}
              <div
                className="w-16 h-16 rounded-full flex items-center justify-center mb-5 transition-transform duration-300 group-hover:scale-110"
                style={{ background: "var(--rose-mist)" }}
              >
                <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                  <path
                    d="M14 3.5V17.5M14 3.5L8.75 8.75M14 3.5L19.25 8.75"
                    stroke="var(--rose)"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M3.5 17.5V21C3.5 22.933 5.067 24.5 7 24.5H21C22.933 24.5 24.5 22.933 24.5 21V17.5"
                    stroke="var(--rose)"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>

              <span
                className="font-body text-[15px] font-medium"
                style={{ color: "var(--foreground)" }}
              >
                Выберите фотографии
              </span>
              <span
                className="font-body text-[13px] mt-1"
                style={{ color: "var(--muted-foreground)" }}
              >
                JPG, PNG — до 10 МБ • до {MAX_PHOTOS} фото
              </span>
            </div>
          </div>
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={handleFileChange}
        />

        {/* Main CTA */}
        <div className="mt-5 w-full max-w-[320px] animate-fade-up delay-600">
          {selectedFiles.length > 0 ? (
            <Button
              className="w-full h-[54px] rounded-[16px] text-[16px] font-medium font-body transition-all duration-300 hover:scale-[1.02] active:scale-[0.98]"
              style={{
                background: "linear-gradient(145deg, var(--rose) 0%, var(--mauve) 100%)",
                color: "white",
                boxShadow: "0 8px 32px oklch(0.48 0.14 12 / 0.3)",
                border: "none"
              }}
              onClick={handleAnalyze}
            >
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" className="mr-2">
                <path d="M10 2L2 10L10 18M2 10H18" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" transform="rotate(180 10 10)" />
              </svg>
              Анализировать {selectedFiles.length} {selectedFiles.length === 1 ? "фото" : selectedFiles.length < 5 ? "фото" : "фото"}
            </Button>
          ) : (
            <Button
              className="w-full h-[54px] rounded-[16px] text-[16px] font-medium font-body transition-all duration-300 hover:scale-[1.02] active:scale-[0.98]"
              style={{
                background: "linear-gradient(145deg, var(--rose) 0%, var(--mauve) 100%)",
                color: "white",
                boxShadow: "0 8px 32px oklch(0.48 0.14 12 / 0.3)",
                border: "none"
              }}
              onClick={() => fileInputRef.current?.click()}
            >
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" className="mr-2">
                <rect x="2" y="4" width="16" height="12" rx="2" stroke="white" strokeWidth="1.5" />
                <circle cx="10" cy="10" r="3" stroke="white" strokeWidth="1.5" />
                <circle cx="14.5" cy="6.5" r="1" fill="white" />
              </svg>
              Загрузить фото
            </Button>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="py-5 px-6 animate-fade-up delay-800">
        <div className="flex items-center justify-center gap-6">
          {[
            { num: "13", label: "типажей" },
            { num: "5", label: "групп" },
            { num: "87%", label: "точность" },
          ].map((stat, i) => (
            <div key={i} className="text-center">
              <div
                className="font-editorial text-[22px]"
                style={{ color: "var(--rose)", fontWeight: 600 }}
              >
                {stat.num}
              </div>
              <div
                className="font-body text-[11px] uppercase tracking-[0.08em]"
                style={{ color: "var(--muted-foreground)" }}
              >
                {stat.label}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* History section */}
      {history.length > 0 && (
        <div className="px-5 pb-8 animate-fade-up delay-1000">
          <div className="flex items-center justify-between mb-3">
            <h2
              className="font-editorial text-[22px]"
              style={{ fontWeight: 500, color: "var(--foreground)" }}
            >
              История анализов
            </h2>
            <span className="font-body text-[12px]" style={{ color: "var(--muted-foreground)" }}>
              {history.length} {history.length === 1 ? "анализ" : history.length < 5 ? "анализа" : "анализов"}
            </span>
          </div>
          <div className="space-y-2.5">
            {history.map((item) => (
              <button
                key={item.id}
                onClick={() => onOpenResult(item.id)}
                className="w-full flex items-center gap-3.5 p-3 rounded-[16px] text-left transition-all duration-200 hover:scale-[1.01] active:scale-[0.99]"
                style={{ background: "var(--card)", border: "1px solid var(--border)" }}
              >
                {/* Thumbnail */}
                <div className="w-[52px] h-[52px] rounded-[12px] overflow-hidden shrink-0">
                  {item.photoDataUrls[0] ? (
                    <img src={item.photoDataUrls[0]} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full" style={{ background: "var(--muted)" }} />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-body text-[14px] font-semibold truncate" style={{ color: "var(--foreground)" }}>
                    {item.kibbeType.nameRu}
                  </div>
                  <div className="font-body text-[12px] truncate" style={{ color: "var(--muted-foreground)" }}>
                    {item.kibbeType.name} • {item.confidence}%
                  </div>
                  <div className="font-body text-[11px] mt-0.5" style={{ color: "var(--muted-foreground)" }}>
                    {formatDate(item.date)}
                  </div>
                </div>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="shrink-0">
                  <path d="M6 4L10 8L6 12" stroke="var(--muted-foreground)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
